/**
 * The drawer's "..." menu: the file actions (download, replace, move, ask the
 * agent to read it again, remove) and whatever the PDF's Word copy offers.
 * Readers get the downloads only.
 */
import {
  Download,
  FolderInput,
  MoreHorizontal,
  Sparkles,
  Trash2,
  Upload
} from "lucide-react";
import type { FC, ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

type Props = {
  canEdit: boolean;
  busy: boolean;
  onDownload: () => void;
  onReplace: () => void;
  onReread: () => void;
  onRemove: () => void;
  /** Opens Move To… for this entry; absent for readers. */
  onMove?: () => void;
  /** The Word copy's items, each group led by its own separator. */
  conversionItems?: ReactNode;
};

export const EntryActionsMenu: FC<Props> = ({
  canEdit,
  busy,
  onDownload,
  onReplace,
  onReread,
  onRemove,
  onMove,
  conversionItems
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Entry Actions"
        data-testid="entry-actions"
      >
        <MoreHorizontal />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-56">
      <DropdownMenuItem disabled={busy} onSelect={onDownload}>
        <Download /> Download
      </DropdownMenuItem>
      {canEdit ? (
        <>
          <DropdownMenuItem
            disabled={busy}
            onSelect={onReplace}
            data-testid="entry-replace"
          >
            <Upload /> Replace File…
          </DropdownMenuItem>
          {onMove ? (
            <DropdownMenuItem onSelect={onMove} data-testid="entry-move">
              <FolderInput /> Move To…
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            disabled={busy}
            onSelect={onReread}
            data-testid="entry-reread"
          >
            <Sparkles /> Ask Casey to Re-read
          </DropdownMenuItem>
        </>
      ) : null}
      {conversionItems}
      {canEdit ? (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            disabled={busy}
            onSelect={onRemove}
            data-testid="delete-entry"
          >
            <Trash2 /> Remove…
          </DropdownMenuItem>
        </>
      ) : null}
    </DropdownMenuContent>
  </DropdownMenu>
);
